import { ILegalTransactionType } from '../Interfaces/ILegalTransactionType';

/**
 * Fuzzy-Matcher fuer Vorgangsart-Vorschlaege beim DialogInbox-Import.
 *
 * Quelle ist der ``transactionTypeHint`` aus dem Import-Payload (z.B. die
 * caseTypes[].displayName beim SDS-Import oder die ``Art=...``-Section
 * aus dem XJustiz-DiNo-Container). Auf der Zielmaschine sind die
 * Vorgangsarten oft umbenannt, gekuerzt oder mit/ohne Klammerzusatz
 * gepflegt — wir wollen trotzdem den besten Vorschlag liefern.
 *
 * Heuristik:
 *  - Token-Overlap (case-insensitive)
 *  - Bekannte Notar-Abkuerzungen werden in beide Richtungen aufgeloest
 *    (KV ↔ Kaufvertrag, GmbH ↔ Gesellschaft mit beschraenkter Haftung, …)
 *  - Substring-Match liefert Bonus
 */

const ABBREVIATIONS: Record<string, string[]> = {
  kv: ['kaufvertrag'],
  ev: ['erbvertrag'],
  gv: ['gesellschaftsvertrag'],
  hr: ['handelsregister'],
  ur: ['urkunde'],
  ggv: ['grundstuecksgesellschaftsvertrag'],
  weg: ['wohnungseigentum'],
  nak: ['notaranderkonto'],
  vm: ['vollmacht'],
  vv: ['vorsorgevollmacht'],
  tv: ['testament'],
  rv: ['rentenvertrag'],
  mv: ['mietvertrag'],
  esa: ['erbscheinsantrag'],
  gmbh: ['gesellschaft', 'mit', 'beschraenkter', 'haftung'],
  kg: ['kommanditgesellschaft'],
  ag: ['aktiengesellschaft'],
  ug: ['unternehmergesellschaft'],
  gbr: ['gesellschaft', 'buergerlichen', 'rechts'],
  ohg: ['offene', 'handelsgesellschaft'],
  ek: ['einzelkaufmann'],
  geb: ['geboren'],
};

const _normalize = (s: string): string =>
  s
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .trim();

const _tokenize = (s: string): string[] =>
  _normalize(s)
    .replace(/[(),.\-_/&+]/g, ' ')
    .split(/\s+/)
    .filter(Boolean);

const _expandTokens = (tokens: string[]): Set<string> => {
  const out = new Set<string>(tokens);
  // Abkuerzung -> Vollform
  for (const t of tokens) {
    const expansion = ABBREVIATIONS[t];
    if (expansion) expansion.forEach(e => out.add(e));
  }
  // Vollform -> Abkuerzung (alle Bestandteile muessen vorkommen)
  for (const [abbr, full] of Object.entries(ABBREVIATIONS)) {
    if (full.every(f => out.has(f))) out.add(abbr);
  }
  return out;
};

const _score = (hint: string, candidate: string): number => {
  if (!hint || !candidate) return 0;
  const hNorm = _normalize(hint);
  const cNorm = _normalize(candidate);
  if (hNorm === cNorm) return 1000;

  const hTokens = _expandTokens(_tokenize(hint));
  const cTokens = _expandTokens(_tokenize(candidate));
  if (hTokens.size === 0 || cTokens.size === 0) return 0;

  let matches = 0;
  hTokens.forEach(t => {
    if (cTokens.has(t)) matches++;
  });
  if (matches === 0) return 0;

  // Coverage: Anteil der Hint-Tokens, die im Candidate vorkommen.
  // Wir bevorzugen Treffer, bei denen der Hint moeglichst vollstaendig
  // im Candidate aufgeht (typische Notar-Bezeichnungen sind laenger als
  // die importierte Kurzform).
  const coverage = matches / hTokens.size;
  let score = coverage * 70;

  // Substring-Bonus (eines im anderen)
  if (cNorm.includes(hNorm) || hNorm.includes(cNorm)) {
    score += 30;
  }

  // Bonus, wenn die Anzahl signifikanter Tokens beider Seiten aehnlich ist
  const sizeDelta = Math.abs(hTokens.size - cTokens.size);
  if (sizeDelta <= 1) score += 5;

  return score;
};

export interface ILegalTransactionTypeSuggestion {
  type: ILegalTransactionType;
  /** 0..100+ — je hoeher, desto sicherer der Match. */
  score: number;
}

/**
 * Liefert die besten Vorgangsart-Vorschlaege (sortiert).
 *
 * @param hint   z.B. "KV WEG (ohne NAK)" — aus dem Import
 * @param types  Vorgangsarten der Zielmaschine
 * @param min    Mindest-Score; default 25 (= ein nicht-triviales Token-Match)
 * @param limit  Maximale Anzahl Vorschlaege
 */
export const suggestLegalTransactionTypes = (
  hint: string | null | undefined,
  types: ILegalTransactionType[] | undefined,
  min = 25,
  limit = 5
): ILegalTransactionTypeSuggestion[] => {
  if (!hint || !types || types.length === 0) return [];
  const scored: ILegalTransactionTypeSuggestion[] = [];
  for (const t of types) {
    const s = _score(hint, t.LegalTransactionType || '');
    if (s >= min) scored.push({ type: t, score: s });
  }
  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, limit);
};

/** Convenience: nur der beste Vorschlag, oder null. */
export const bestLegalTransactionTypeMatch = (
  hint: string | null | undefined,
  types: ILegalTransactionType[] | undefined
): ILegalTransactionType | null => {
  const suggestions = suggestLegalTransactionTypes(hint, types, 25, 1);
  return suggestions[0]?.type ?? null;
};
